import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import {
  LoadDataAction,
  EntityState,
  DataPayload,
  SortPayloadAction,
} from '../types';
import { AxiosInstance } from 'axios';
import { CompanyEntity, CompanyFilters, SalesModelEnum } from './types';
import CompaniesApi from '../../api/companiesApi';
import { Auth } from '../auth/types';
import { JourneyTypeEnum } from '../journeys/types';
import { RootState } from '../store';
import { FeatureAccess } from '../../config/companyFeatures';

export interface CompanyState extends EntityState<CompanyEntity> {
  selectedFilters?: CompanyFilters;
}

const initialState: CompanyState = {
  data: [],
  currentPage: 0,
  // right now we return all results, so this isn't accurate...
  resultsPerPage: 8,
  totalPages: 0,
  sortColumn: 'companyName',
  sortDirection: -1,
  selectedFilters: {},
};

// right now we fetch all companies at once. will need to change when we have too many companies for one page
const companySlice = createSlice({
  name: 'companies',
  initialState,
  reducers: {
    setCompanies: (
      state,
      { payload }: PayloadAction<DataPayload<CompanyEntity>>
    ) => {
      state.data = payload.entities;
      state.totalPages = payload.totalPages;
    },
    sortCompanies: (state, { payload }: SortPayloadAction<CompanyEntity>) => {
      state.sortColumn = payload.column;
      state.sortDirection = payload.direction;
    },
    addCompany: (
      state,
      {
        payload,
      }: PayloadAction<{
        companyName: string;
        salesModel: SalesModelEnum;
        defaultJourneyType: JourneyTypeEnum;
        id: string;
        parentCompany?: string;
        featureAccess?: FeatureAccess;
      }>
    ) => {
      state.data = [
        {
          companyName: payload.companyName,
          salesModel: payload.salesModel,
          defaultJourneyType: payload.defaultJourneyType,
          _id: payload.id,
          parentCompany: payload.parentCompany,
          orgType: payload.parentCompany ? 'child' : undefined,
          // Without this the store says a just-created Part 11 company is
          // unregulated, so useReasonRequired is false and the Audit Trail nav
          // hides — until something refetches.
          featureAccess: payload.featureAccess,
        },
        ...state.data,
      ];
    },
    setCurrentPage: (state, { payload }: PayloadAction<number>) => {
      state.currentPage = payload;
    },
    resetPage: (state) => {
      state.selectedFilters = initialState.selectedFilters;
      state.sortColumn = initialState.sortColumn;
      state.sortDirection = initialState.sortDirection;
      state.currentPage = initialState.currentPage;
    },
  },
});

export const loadCompanies: LoadDataAction<CompanyEntity> = async (
  axios: AxiosInstance,
  auth: Auth,
  state: CompanyState
) => {
  const { sortColumn, sortDirection } = state;

  const companyData = await CompaniesApi.getCompanies(axios, auth);
  const sortedEntities = companyData.entities.sort(
    (a, b) =>
      (a[sortColumn] || '')
        .toString()
        .localeCompare((b[sortColumn] || '').toString()) * sortDirection
  );

  return setCompanies({
    entities: sortedEntities,
    totalPages: companyData.totalPages,
  });
};

export const selectIsCaas = (state: RootState) => {
  const company = state.companies.data[0];
  return company !== undefined && company.salesModel == SalesModelEnum.CaaS;
};

export const {
  setCompanies,
  sortCompanies,
  addCompany,
  setCurrentPage,
  resetPage,
} = companySlice.actions;

export default companySlice.reducer;
